import { Divider, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import ChevronDownIcon from '@/components/Icons/ChevronDownIcon';
import TrashIcon from '@/components/Icons/TrashIcon';
import { TeamMemberTypeRoleEnum } from '@/graphql';

import { MenuConfigs, RolesMenuProps } from './types';

const WIDTH = 140;

const menuConfigs: Partial<MenuConfigs> = {
  [TeamMemberTypeRoleEnum.TeamAdmin]: {
    title: 'Team admin',
    description: 'Can manage members within the team',
  },
  [TeamMemberTypeRoleEnum.TeamMember]: {
    title: 'Team member',
    description: 'Can create and edit models',
  },
};

function RolesMenu({ value, label, onChange, loading = false, team, onRemove }: RolesMenuProps) {
  return (
    <Stack width={WIDTH}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        fullWidth
        data-cy="select-team-member-role"
        size="medium"
        value={value}
        IconComponent={ChevronDownIcon}
        onChange={onChange}
        displayEmpty
        renderValue={(role) => {
          if (loading) return <Skeleton width={45} />;

          if (!role)
            return (
              <Typography variant="body1" color="text.disabled" lineHeight="inherit">
                Select team member role
              </Typography>
            );

          return (
            <Typography variant="body1" color="text.default" lineHeight="inherit">
              {menuConfigs[role].title}
            </Typography>
          );
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
      >
        <Typography variant="h6" color="text.subtle" sx={{ p: 1 }}>
          Select a team member role
        </Typography>
        {Object.entries(menuConfigs).map(([role, config]) => (
          <MenuItem data-cy={`role-option-${role.toLowerCase()}`} key={role} value={role}>
            <Box sx={{ maxWidth: 250, whiteSpace: 'normal' }}>
              <Typography variant="h5">{config.title}</Typography>
              <Typography color="text.muted" variant="body2">
                {config.description}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        {team && (
          <>
            <Divider sx={{ my: -1 }} />
            <MenuItem disabled={team.default} onClick={() => onRemove?.(team.id)}>
              <Stack direction="row" alignItems="center" gap={1}>
                <TrashIcon color="secondary" />
                <Typography variant="h5">Remove from team</Typography>
              </Stack>
            </MenuItem>
          </>
        )}
      </Select>
    </Stack>
  );
}

export default RolesMenu;
